import { ChevronDown, Info, Lock, Trash2, Unlink, UserPlus } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { NewCaseDialog } from "@/features/accident-person";
import {
  ACCIDENT_ROLES, NOBODY_LINKED, SAME_EMAIL_CAVEAT, accidentSections, canSay, canUnlink, crashLine, personText,
  relationText, roleText, saidText, whyText,
} from "@/lib/accident";
import { dialWithFor, listsOf, onList } from "@/lib/accident-person";
import type { DialWith } from "@/lib/accident-person";
import { ApiError } from "@/lib/api";
import { contactName, stageText } from "@/lib/contacts";
import {
  useContact, useContactFields, useContacts, useDelistContact, useEnlistContact, useLeadAccident, useLinkAccident,
  useUnlinkAccident,
} from "@/lib/queries";
import { fieldText } from "@/lib/record-fields";
import { DELETED_NOTE, NOT_YOURS, initials } from "@/lib/same-phone";
import { ACCIDENT_CARD_ID } from "@/lib/seen-before";
import type { AccidentRecord, AccidentRole, ContactRow, LeadAccident, LeadCard } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";
import { cn, formatPhoneNumber, relativeTime } from "@/lib/utils";

/**
 * How a contact's records relate, as a person says (ACL-337, ADR-120 in the platform repo): the same
 * accident again, another accident of the same person, or another person in the same accident. Read
 * from its own route (`useLeadAccident`), never the record's, so a fault here leaves the record and the
 * call's card as they were; an older core-api without it (404) draws nothing.
 *
 * Nothing is said for the rep. A record with the phone (the card's own Same phone list, ACL-336) or the
 * e-mail is listed as "not said yet" until somebody who may open it says "same accident" or "another
 * accident"; someone else in the accident is linked by a search. The server writes a note on both
 * records each time. Whoever may open a record may also take it out again, a rep included.
 */

/** The phone's and the e-mail's records each get one: how the record relates to this one, who said
 *  so, and, while nobody has, the two answers. `compact` on the phone: the mark alone. */
export function AccidentMark({
  leadId, record, compact = false,
}: {
  leadId: string;
  record: AccidentRecord | undefined;
  compact?: boolean;
}) {
  const toast = useToast();
  const link = useLinkAccident();
  if (!record || record.deleted) return null;
  const said = saidText(record);
  const role = roleText(record.role);
  const busy = link.isPending;
  const answer = (relation: "same_accident" | "another_accident") =>
    link.mutate(
      { leadId, otherId: record.id, relation },
      {
        onSuccess: () => toast.success(relation === "same_accident" ? "Marked the same accident" : "Marked another accident"),
        onError: (e) => toast.error("Could not save it", e instanceof ApiError ? e.message : "Please try again."),
      },
    );

  return (
    <div className={cn("mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1", compact ? "text-2xs" : "text-xs")}>
      <Badge
        tone={record.relation === "same_accident" ? "accent" : record.relation === "another_accident" ? "neutral" : "outline"}
        size="sm"
      >
        {relationText(record.relation)}
        {role && ` · ${role}`}
      </Badge>
      {said && (
        <span className="text-text-muted" title={record.linked_at ?? undefined}>
          {said}
          {record.linked_at && ` · ${relativeTime(record.linked_at)}`}
        </span>
      )}
      {!compact && canSay(record) && (
        <span className="flex gap-1.5" aria-label="Say how this record relates">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => answer("same_accident")}>
            Same accident
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => answer("another_accident")}>
            Another accident
          </Button>
        </span>
      )}
      {!compact && canUnlink(record) && (
        <UnlinkButton leadId={leadId} otherId={record.id} name={record.may_open ? contactName(record) : null} disabled={busy} />
      )}
    </div>
  );
}

/** Takes `otherId` out of its accident, from `leadId`'s page, after asking: the record starts an
 *  accident of its own, with a copy of the crash's details (ADR-222). The page's own id takes the record
 *  itself out. */
function UnlinkButton({
  leadId, otherId, name, number, disabled, label = "Unlink",
}: {
  leadId: string;
  otherId: string;
  /** Who is taken out, for the question; the record's own line says "this record". */
  name?: string | null;
  number?: string | null;
  disabled?: boolean;
  label?: string;
}) {
  const toast = useToast();
  const unlink = useUnlinkAccident();
  const [asking, setAsking] = React.useState(false);
  const own = otherId === leadId;
  const who = own ? "this record" : (name ?? "this record");
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        aria-label={own ? "Unlink this record" : undefined}
        disabled={disabled || unlink.isPending}
        onClick={() => setAsking(true)}
      >
        <Unlink />
        {label}
      </Button>
      <ConfirmDialog
        open={asking}
        onOpenChange={setAsking}
        title={`Take ${who} out of ${number ? `accident ${number}` : "this accident"}?`}
        description={
          <>
            {own ? "It" : "They"} will start an accident of {own ? "its" : "their"} own, with a copy of the crash's details,
            and both records get a note saying you did it. The rest of the accident stays as it is.
          </>
        }
        confirmLabel="Take out"
        destructive
        loading={unlink.isPending}
        onConfirm={() =>
          unlink.mutate(
            { leadId, otherId },
            {
              onSuccess: () => {
                setAsking(false);
                toast.success("Taken out of the accident");
              },
              onError: (e) => {
                setAsking(false);
                toast.error("Could not take them out", e instanceof ApiError ? e.message : "Please try again.");
              },
            },
          )
        }
      />
    </>
  );
}

/**
 * The record's Accident card (ACL-326): the accident by its number, the crash's facts on one line
 * (shared by everyone in it, ADR-221), who is in the crash with their role and stage (this record first),
 * and Add a party/case: someone new or a new case for this person (the New case dialog, which asks
 * which accident) or a contact already here. Then the
 * records with the e-mail on another phone, to be said of. Read from its own route (`useLeadAccident`),
 * never the record's, so a fault here leaves the record and the call's card as they were; an older
 * core-api without it (404) draws nothing. Taking anyone out asks first.
 */
export function AccidentCard({ leadId, source }: { leadId: string; source?: LeadCard }) {
  const accident = useLeadAccident(leadId, { poll: true });
  const fields = useContactFields(leadId);
  const [linking, setLinking] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  if (!accident.data) return null;
  const { byPhone, byEmail, linked } = accidentSections(accident.data);
  // Everyone in the accident, a record with the phone said to be in it too: a child added on their
  // parent's number (ACL-510) is listed by the phone.
  const inAccident = [...linked, ...[...byPhone.values()].filter((record) => record.relation === "same_accident")];
  const own = accident.data.accident;
  const number = own?.number ?? null;
  const facts = crashLine(fields.data, fieldText);
  const name = source ? (contactName(source) ?? "This record") : "This record";
  const ownRole = roleText(own?.role);
  const nobody = inAccident.length === 0;
  // On the record there is no call: the list is the one this record is on (ADR-225).
  const dialWith = source ? dialWithFor(source, null) : null;

  return (
    <Card id={ACCIDENT_CARD_ID} className="scroll-mt-4">
      <CardHeader
        className="min-h-15 items-center px-5 py-3.5 [&_h2]:text-lg"
        title={number ? `Accident ${number}` : "Accident"}
        description={facts ?? (own ? "No details of the crash yet." : "This record is in no accident yet.")}
        action={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="text-base">
                <UserPlus />
                Add a party/case
                <ChevronDown className="size-3.5" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {source && (
                <DropdownMenuItem onSelect={() => setAdding(true)}>Someone new, or a new case for {name}</DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => setLinking(true)}>A contact already here</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
      <div className="space-y-6 px-5 pb-5 pt-2.5">
        <section aria-label="In this crash">
          <p className="text-2xs font-semibold uppercase tracking-wide text-text-subtle">In this crash</p>
          <ul className="divide-y divide-border">
            {own && (
              <li className="flex gap-3 py-3">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-3 text-xs font-semibold text-accent-11" aria-hidden>
                  {initials(name) ?? "?"}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-text">{name}</p>
                      <p className="truncate text-xs text-text-muted">This record · {ownRole ? `in accident ${number} as ${ownRole.toLowerCase()}` : `in accident ${number}`}</p>
                    </div>
                    <span className="flex shrink-0 items-center gap-1">
                      {ownRole && <Badge tone="accent" size="sm">{ownRole}</Badge>}
                      {source?.stage && (
                        <Badge tone={stageText(source.stage).tone} size="sm" title={stageText(source.stage).description}>
                          {stageText(source.stage).label}
                        </Badge>
                      )}
                    </span>
                  </div>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-subtle">
                    {own.linked_by && (
                      <span title={own.linked_at ?? undefined}>
                        Said by {own.linked_by}
                        {own.linked_at && ` · ${relativeTime(own.linked_at)}`}
                      </span>
                    )}
                    {/* Alone in its accident there is nothing to take it out of (ADR-222). */}
                    {!nobody && <UnlinkButton leadId={leadId} otherId={leadId} number={number} label="Leave this accident" />}
                  </p>
                </div>
              </li>
            )}
            {inAccident.map((record) => (
              <AccidentRow key={record.id} record={record}>
                {canUnlink(record) && (
                  <UnlinkButton leadId={leadId} otherId={record.id} name={record.may_open ? contactName(record) : null} number={number} />
                )}
              </AccidentRow>
            ))}
          </ul>
          {nobody && (
            <p className="rounded-md border border-border bg-gray-2 px-3 py-3 text-sm leading-5 text-gray-11">{NOBODY_LINKED}</p>
          )}
        </section>

        {byEmail.length > 0 && (
          <section aria-label="Possible, same email">
            <p className="text-2xs font-semibold uppercase tracking-wide text-text-subtle">Possible, same email</p>
            <p className="mt-0.5 flex items-start gap-1.5 text-xs text-text-muted">
              <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              <span>{SAME_EMAIL_CAVEAT}</span>
            </p>
            <ul className="divide-y divide-border">
              {byEmail.map((record) => (
                <AccidentRow key={record.id} record={record}>
                  <AccidentMark leadId={leadId} record={record} />
                </AccidentRow>
              ))}
            </ul>
          </section>
        )}
      </div>
      <LinkSomeoneDialog
        open={linking}
        onOpenChange={setLinking}
        leadId={leadId}
        dialWith={dialWith}
        exclude={new Set([leadId, ...accident.data.records.map((r) => r.id)])}
      />
      {source && (
        <NewCaseDialog open={adding} onOpenChange={setAdding} source={source} dialWith={dialWith} />
      )}
    </Card>
  );
}

/** One record listed on the card: its name where the reader may open it, its firm, stage and role,
 *  its last outcome, and why it is listed. `children` is what may be done about it. */
function AccidentRow({ record, children }: { record: AccidentRecord; children?: React.ReactNode }) {
  const name = record.may_open ? contactName(record) : null;
  const locked = !record.may_open || record.deleted;
  const stage = stageText(record.stage);
  const role = roleText(record.role);
  const firm = record.firm?.label ?? "No firm";
  const outcome = record.disposition_label ?? record.disposition;
  const who = personText(record);

  return (
    <li className="flex gap-3 py-3">
      <span
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
          locked ? "bg-gray-3 text-gray-9" : "bg-accent-3 text-accent-11",
        )}
        aria-hidden
      >
        {record.deleted ? <Trash2 className="size-3.5" /> : locked ? <Lock className="size-3.5" /> : (initials(name) ?? "?")}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {locked ? (
              <p className="truncate text-sm font-medium text-text-muted">
                {record.deleted ? "Deleted record" : "Restricted record"}
              </p>
            ) : (
              <Link
                to={`/contacts/${record.id}`}
                className="block truncate text-sm font-semibold text-text hover:text-accent-11 hover:underline"
              >
                {name ?? "No name"}
              </Link>
            )}
            <p className="truncate text-xs text-text-muted" title={firm}>
              {firm}
            </p>
          </div>
          <span className="flex shrink-0 items-center gap-1">
            {role && record.relation === "same_accident" && (
              <Badge tone="accent" size="sm">
                {role}
              </Badge>
            )}
            <Badge tone={stage.tone} size="sm" title={stage.description}>
              {stage.label}
            </Badge>
          </span>
        </div>
        {outcome && (
          <p className="mt-1 text-xs text-text">
            <span className="text-text-muted">Last outcome </span>
            {outcome}
          </p>
        )}
        <p className="mt-0.5 text-xs text-text-subtle">
          {who && `${who} · `}
          {whyText(record)}
          {record.why === "linked" && record.linked_at && ` · ${relativeTime(record.linked_at)}`}
        </p>
        {locked && <p className="mt-1 text-xs text-text-subtle">{record.deleted ? DELETED_NOTE : NOT_YOURS}</p>}
        {children}
      </div>
    </li>
  );
}

/** Fewer letters than this match half the list: the search waits for a name or a number. */
const MIN_QUERY = 2;
const NO_ROLE = "none";

/**
 * Link someone else in this accident: a passenger, the driver, a guardian. Found by a search, with a
 * role if the rep knows it. Both records get a note saying who linked them; nothing is merged.
 */
/**
 * Link a contact already here into the accident. With a list to name (`dialWith`, ADR-225) the
 * dialog also says whether they are called from it: the box starts as they are, on that list or not
 * (their card's live rows), and a change is written after the link, on the list or off it.
 */
function LinkSomeoneDialog({
  open, onOpenChange, leadId, exclude, dialWith = null,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  /** This record, and every record already listed beside it. */
  exclude: Set<string>;
  dialWith?: DialWith | null;
}) {
  const toast = useToast();
  const link = useLinkAccident();
  const enlist = useEnlistContact();
  const delist = useDelistContact();
  const [text, setText] = React.useState("");
  const [chosen, setChosen] = React.useState<ContactRow | null>(null);
  const [role, setRole] = React.useState<AccidentRole | null>(null);
  // The rep's own answer to "Add them to the dial list"; null until they touch it.
  const [callThem, setCallThem] = React.useState<boolean | null>(null);
  const [busy, setBusy] = React.useState(false);
  const query = useDebounced(text.trim(), 300);
  // The chosen contact's card says which lists they are on now.
  const card = useContact(dialWith && chosen ? chosen.id : undefined);
  const listed = dialWith ? onList(listsOf(card.data), dialWith.campaignId) : false;
  const ticked = callThem ?? listed;
  React.useEffect(() => {
    if (open) {
      setText("");
      setChosen(null);
      setRole(null);
      setCallThem(null);
    }
  }, [open]);

  const run = async () => {
    if (!chosen || busy) return;
    setBusy(true);
    try {
      try {
        await link.mutateAsync({ leadId, otherId: chosen.id, relation: "same_accident", role });
      } catch (e) {
        toast.error("Could not link them", e instanceof ApiError ? e.message : "Please try again.");
        return;
      }
      const who = contactName(chosen) ?? "The contact";
      if (dialWith && card.data && ticked !== listed) {
        try {
          if (ticked) {
            await enlist.mutateAsync({ leadId: chosen.id, campaignId: dialWith.campaignId, afterCall: dialWith.callId });
          } else {
            await delist.mutateAsync({ leadId: chosen.id, campaignId: dialWith.campaignId });
          }
        } catch (e) {
          toast.error(
            ticked ? `${who} is linked, but not on the dial list` : `${who} is linked, but still on the dial list`,
            e instanceof ApiError ? e.message : "Please try again.",
          );
          onOpenChange(false);
          return;
        }
      }
      const list = dialWith && ticked !== listed
        ? (ticked ? "On the dial list" : "Off the dial list")
        : null;
      toast.success("Linked", [`${who} is in this accident.`, list].filter(Boolean).join(" "));
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DialogHeader>
          <DialogTitle>Link someone in this accident</DialogTitle>
          <DialogDescription>
            A passenger, the driver, a guardian. Each record then lists the other under Also in this accident,
            and both get a note saying you linked them. Nothing is merged.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Search contacts" htmlFor="accident-link-search">
            <Input
              id="accident-link-search"
              autoFocus
              placeholder="Name or phone number"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setChosen(null);
              }}
            />
          </Field>
          {query.length >= MIN_QUERY ? (
            <Results query={query} exclude={exclude} chosen={chosen} onChoose={(row) => { setChosen(row); setCallThem(null); }} />
          ) : (
            <p className="text-sm text-text-muted">Type at least {MIN_QUERY} letters or digits.</p>
          )}
          <Field label="Their role in the accident" htmlFor="accident-link-role">
            <Select value={role ?? NO_ROLE} onValueChange={(value) => setRole(value === NO_ROLE ? null : (value as AccidentRole))}>
              <SelectTrigger id="accident-link-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ROLE}>Not said</SelectItem>
                {ACCIDENT_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {roleText(r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {dialWith && chosen && (
            <div className="rounded-md border border-border px-3 py-2.5">
              <label htmlFor="accident-link-call-them" className="flex cursor-pointer items-center gap-2.5 text-sm text-text">
                <input
                  id="accident-link-call-them"
                  type="checkbox"
                  className="size-3.5 shrink-0 accent-accent-9"
                  checked={ticked}
                  disabled={!card.data}
                  onChange={(e) => setCallThem(e.target.checked)}
                />
                <span>Add them to the dial list</span>
              </label>
              <p className="mt-1 pl-6 text-xs text-text-muted">
                {!card.data
                  ? "Reading their record…"
                  : listed && ticked
                    ? "On the dial list already; untick to take them off it."
                    : listed && !ticked
                      ? "Taken off the dial list when linked."
                      : ticked
                        ? dialWith.callId
                          ? "On the dial list when linked; the dialer calls them once this call is over."
                          : "On the dial list when linked; the dialer calls them."
                        : "Not on the dial list: nobody dials them unless a supervisor lists them."}
              </p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button loading={busy} disabled={chosen === null} onClick={() => void run()}>
            Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Mounted only while there is something to search for, so a closed dialog reads nothing. */
function Results({
  query, exclude, chosen, onChoose,
}: {
  query: string;
  exclude: Set<string>;
  chosen: ContactRow | null;
  onChoose: (row: ContactRow) => void;
}) {
  const contacts = useContacts({ q: query });
  if (contacts.isLoading) return <Spinner />;
  const rows = (contacts.data?.items ?? []).filter((row) => !exclude.has(row.id));
  if (rows.length === 0) return <p className="text-sm text-text-muted">No other contact found for “{query}”.</p>;
  return (
    <ul role="listbox" aria-label="Contacts" className="max-h-72 space-y-1 overflow-y-auto">
      {rows.map((row) => (
        <li key={row.id}>
          <button
            type="button"
            role="option"
            aria-selected={chosen?.id === row.id}
            onClick={() => onChoose(row)}
            className={cn(
              "w-full rounded-md border px-3 py-2 text-left text-sm hover:bg-surface-hover",
              chosen?.id === row.id ? "border-accent-8 bg-accent-2" : "border-border",
            )}
          >
            <span className="font-medium text-text">{contactName(row) ?? "No name"}</span>
            <span className="text-text-muted"> · {formatPhoneNumber(row.phone)}</span>
            <span className="block text-xs text-text-muted">
              {[row.firm?.label, stageText(row.stage).label, `came in ${relativeTime(row.created_at)}`]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** On the call: who else is in the caller's accident, by name where the rep may open them, with their
 *  role. Read-only; saying and linking are on the record, one click away. */
export function AccidentOnCall({ accident }: { accident: LeadAccident | undefined }) {
  const { linked } = accidentSections(accident);
  if (linked.length === 0) return null;
  return (
    <section aria-label="In this accident" className="mt-2 border-t border-border pt-2">
      <p className="text-2xs font-semibold uppercase tracking-wide text-text-subtle">In this accident</p>
      <ul className="mt-1 space-y-0.5">
        {linked.map((record) => {
          const name = record.may_open ? contactName(record) : null;
          const role = roleText(record.role);
          return (
            <li key={record.id} className="flex min-w-0 items-center justify-between gap-2">
              <span className={cn("truncate", name ? "text-text" : "text-text-muted")}>
                {name ?? (record.deleted ? "Deleted record" : "Restricted record")}
              </span>
              <span className="flex shrink-0 items-center gap-1">
                {role && (
                  <Badge tone="accent" size="sm">
                    {role}
                  </Badge>
                )}
                <Badge tone={stageText(record.stage).tone} size="sm">
                  {stageText(record.stage).label}
                </Badge>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
